import React, { useEffect } from "react"
import { Button } from "../../components/ui/button"
import { Card, CardHeader, CardContent, CardTitle } from "../../components/ui/card"
import { Input } from "../../components/ui/input"
import { ArrowRight, TrendingUp, LineChart, Zap } from "lucide-react"
import { useNavigate } from "react-router-dom"

export default function LandingPage() {
  const navigate = useNavigate()

  // ✅ 로그인 상태면 자동으로 대시보드 이동
  useEffect(() => {
    const token = localStorage.getItem("access_token")
    if (token) navigate("/admin/default")
  }, [navigate])

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-100 via-pink-100 to-sky-100 dark:from-[#0a0a0a] dark:via-[#111] dark:to-[#080808] text-gray-900 dark:text-gray-100 scroll-smooth">

      {/* ===== Navbar ===== */}
      <nav className="flex justify-between items-center px-8 py-6 max-w-7xl mx-auto">
        <h1
          onClick={() => navigate("/landing")}
          className="text-xl font-bold tracking-tight cursor-pointer hover:text-sky-500 transition"
        >
          ChiSquare
        </h1>
        <div className="flex gap-6 text-sm">
          <a href="#features" className="hover:text-gray-500 transition">Features</a>
          <a href="#pricing" className="hover:text-gray-500 transition">Pricing</a>
          <a href="/sign-in" className="hover:text-gray-500 transition">Sign in</a>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              const token = localStorage.getItem("access_token")
              if (token) navigate("/admin/default")
              else navigate("/sign-in")
            }}
            className="font-medium text-sky-500 hover:text-sky-600 transition"
          >
            Dashboard →
          </a>
        </div>
      </nav>

      {/* ===== Hero Section ===== */}
      <section className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between px-8 py-24 gap-12">
        {/* Left side */}
        <div className="flex-1 text-left">
          <h2 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
            Forecast your business{" "}
            <span className="text-sky-500">smarter with AI.</span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-lg mb-8 max-w-lg">
            ChiSquare predicts demand, optimizes inventory, and helps you make data-driven growth decisions — all automatically.
          </p>
          <div className="flex gap-2 max-w-md">
            <Input
              placeholder="Enter your email"
              className="bg-white/80 dark:bg-neutral-900 border-gray-300 dark:border-neutral-700"
            />
            <Button
              onClick={() => navigate("/sign-up")}
              className="bg-sky-600 hover:bg-sky-500 text-white font-semibold"
            >
              Start free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Right side - Dashboard preview */}
        <div className="flex-1 flex justify-center">
          <Card className="w-[360px] shadow-2xl border border-gray-200 dark:border-neutral-800 bg-white/90 dark:bg-neutral-950/90 backdrop-blur-md">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Predicted Sales</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-xl bg-gradient-to-tr from-sky-400 via-emerald-300 to-amber-300 dark:from-sky-700 dark:via-emerald-600 dark:to-amber-600 p-4 mb-4">
                <div className="bg-white/80 dark:bg-neutral-950/80 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Next Week</p>
                  <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">$4,230</p>
                </div>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Growth rate <span className="text-emerald-500 font-medium">+6.7%</span> from last week
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ===== Features Section ===== */}
      <section id="features" className="max-w-6xl mx-auto px-8 py-20">
        <h3 className="text-3xl font-bold text-center mb-12">
          Why businesses choose <span className="text-sky-500">ChiSquare</span>
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="bg-white/80 dark:bg-neutral-900 border-gray-200 dark:border-neutral-800 hover:border-sky-500 transition">
            <CardContent className="p-6 text-center">
              <TrendingUp className="mx-auto mb-3 text-sky-500" size={28} />
              <h4 className="text-lg font-semibold mb-2">AI-Driven Forecasts</h4>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Predict weekly sales automatically using your product data and trends.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/80 dark:bg-neutral-900 border-gray-200 dark:border-neutral-800 hover:border-sky-500 transition">
            <CardContent className="p-6 text-center">
              <LineChart className="mx-auto mb-3 text-emerald-500" size={28} />
              <h4 className="text-lg font-semibold mb-2">Smart Insights</h4>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Detect hidden patterns — see what drives growth and adjust in real time.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/80 dark:bg-neutral-900 border-gray-200 dark:border-neutral-800 hover:border-sky-500 transition">
            <CardContent className="p-6 text-center">
              <Zap className="mx-auto mb-3 text-amber-500" size={28} />
              <h4 className="text-lg font-semibold mb-2">Actionable Alerts</h4>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Get notified instantly when inventory is low or sales spike unexpectedly.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ===== Pricing Section ===== */}
      <section id="pricing" className="max-w-6xl mx-auto px-8 py-20">
        <h3 className="text-3xl font-bold text-center mb-12">
          Simple, transparent pricing
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          {/* Starter */}
          <Card className="bg-white/80 dark:bg-neutral-900 border-gray-200 dark:border-neutral-800">
            <CardContent className="p-6 text-center">
              <h4 className="text-lg font-semibold mb-2">Starter</h4>
              <p className="text-3xl font-bold mb-2">$0</p>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                1 product • weekly forecast • basic model
              </p>
              <Button
                onClick={() => navigate("/sign-up")}
                variant="outline"
                className="w-full"
              >
                Start free
              </Button>
            </CardContent>
          </Card>

          {/* Standard */}
          <Card className="bg-white/80 dark:bg-neutral-900 border-gray-200 dark:border-neutral-800">
            <CardContent className="p-6 text-center">
              <h4 className="text-lg font-semibold mb-2">Standard</h4>
              <p className="text-3xl font-bold mb-2">
                $9<span className="text-base font-normal">/mo</span>
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                Up to 5 products • twice-a-week forecast • basic alerts
              </p>
              <Button className="w-full bg-sky-600 hover:bg-sky-500 text-white">
                Upgrade
              </Button>
            </CardContent>
          </Card>

          {/* Pro */}
          <Card className="bg-white/90 dark:bg-neutral-950 border border-sky-600">
            <CardContent className="p-6 text-center">
              <h4 className="text-lg font-semibold mb-2 text-sky-500">Pro</h4>
              <p className="text-3xl font-bold mb-2">
                $19<span className="text-base font-normal">/mo</span>
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                Up to 50 products • daily forecasts • smart alerts • strategy tips
              </p>
              <Button className="w-full bg-sky-600 hover:bg-sky-500 text-white">
                For growing teams
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Enterprise */}
        <p className="text-center text-gray-500 dark:text-gray-400 text-sm mt-6">
          Need a custom or enterprise plan?{" "}
          <a href="mailto:team@chisquare.io" className="text-sky-500 hover:text-sky-400">
            Contact us
          </a>
        </p>
      </section>

      {/* ===== CTA Section ===== */}
      <section className="bg-gradient-to-r from-sky-600 to-indigo-600 text-white text-center py-20 px-8">
        <h3 className="text-3xl font-semibold mb-4">Ready to forecast smarter?</h3>
        <p className="text-lg opacity-90 mb-8">
          Start with the free Starter plan — no credit card required.
        </p>
        <Button
          onClick={() => navigate("/sign-up")}
          size="lg"
          className="bg-white text-sky-700 hover:bg-gray-200"
        >
          Start free with Starter <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </section>

      {/* ===== Footer ===== */}
      <footer className="text-center py-10 text-sm text-gray-500">
        © {new Date().getFullYear()} ChiSquare Inc.  
        <br />
        651 N Broad St, Middletown, DE 19709  
        <div className="mt-2">
          <a href="/terms" className="hover:text-gray-400">Terms</a> ·{" "}
          <a href="/privacy" className="hover:text-gray-400">Privacy</a> ·{" "}
          <a href="mailto:team@chisquare.io" className="hover:text-gray-400">team@chisquare.io</a>
        </div>
      </footer>
    </div>
  )
}